import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import { auth, db, storage, ADMIN_EMAIL } from '../../firebase/config';
import {
  FaArrowLeft,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSpinner,
  FaImage,
  FaSave,
  FaTimes,
  FaRobot,
  FaBrain,
  FaIndustry,
  FaGripVertical,
  FaGlobe,
  FaCogs,
  FaUpload
} from 'react-icons/fa';
import './AdminSolutions.css';

const AdminSolutions = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('rpaTools'); // 'rpaTools', 'technologies', 'industries'
  const [rpaTools, setRpaTools] = useState([]);
  const [technologies, setTechnologies] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [activeTab, setActiveTab] = useState('en');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  // Form data for RPA Tools
  const [rpaFormData, setRpaFormData] = useState({
    name_en: '',
    name_fr: '',
    description_en: '',
    description_fr: '',
    features_en: [''],
    features_fr: [''],
    logoUrl: '',
    imageUrl: '',
    color: '#FA4616',
    order: 0
  });

  // Form data for Technologies
  const [techFormData, setTechFormData] = useState({
    name_en: '',
    name_fr: '',
    description_en: '',
    description_fr: '',
    iconUrl: '',
    technologies: [''],
    order: 0
  });
  const [techIconFile, setTechIconFile] = useState(null);
  const [techIconPreview, setTechIconPreview] = useState('');
  const techIconInputRef = useRef(null);

  // Form data for Industries
  const [industryFormData, setIndustryFormData] = useState({
    industry_en: '',
    industry_fr: '',
    useCases_en: [''],
    useCases_fr: [''],
    iconUrl: '',
    order: 0
  });
  const [industryIconFile, setIndustryIconFile] = useState(null);
  const [industryIconPreview, setIndustryIconPreview] = useState('');
  const industryIconInputRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser && currentUser.email.toLowerCase() === ADMIN_EMAIL.toLowerCase()) {
        setUser(currentUser);
        fetchAllData();
      } else {
        navigate('/admin');
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [navigate]);

  const fetchAllData = async () => {
    try {
      await Promise.all([
        fetchRpaTools(),
        fetchTechnologies(),
        fetchIndustries()
      ]);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data');
    }
  };

  const fetchRpaTools = async () => {
    try {
      const q = query(collection(db, 'solutions_rpa_tools'), orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      const items = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setRpaTools(items);
    } catch (err) {
      console.error('Error fetching RPA tools:', err);
    }
  };

  const fetchTechnologies = async () => {
    try {
      const q = query(collection(db, 'solutions_technologies'), orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      const items = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTechnologies(items);
    } catch (err) {
      console.error('Error fetching technologies:', err);
    }
  };

  const fetchIndustries = async () => {
    try {
      const q = query(collection(db, 'solutions_industries'), orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      const items = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setIndustries(items);
    } catch (err) {
      console.error('Error fetching industries:', err);
    }
  };

  const handleImageChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'logo') {
          setLogoFile(file);
          setLogoPreview(reader.result);
        } else {
          setImageFile(file);
          setImagePreview(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleTechIconChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.includes('svg') && !file.name.endsWith('.svg')) {
      setError('Please upload an SVG file.');
      return;
    }
    if (file.size > 500 * 1024) {
      setError('File size must be under 500KB.');
      return;
    }
    setTechIconFile(file);
    setError('');
    const reader = new FileReader();
    reader.onload = (ev) => setTechIconPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleIndustryIconChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError('File size must be under 2MB.');
      return;
    }
    setIndustryIconFile(file);
    setError('');
    const reader = new FileReader();
    reader.onload = (ev) => setIndustryIconPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const uploadImage = async (file, folder) => {
    try {
      const fileName = `solutions/${folder}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fileName);
      const snapshot = await uploadBytes(storageRef, file);
      const url = await getDownloadURL(snapshot.ref);
      return url;
    } catch (err) {
      console.error('Upload error:', err);
      throw err;
    }
  };

  const deleteImage = async (imageUrl) => {
    if (imageUrl && imageUrl.includes('firebase')) {
      try {
        const imageRef = ref(storage, imageUrl);
        await deleteObject(imageRef);
      } catch (err) {
        console.log('Image deletion error:', err);
      }
    }
  };

  // RPA Tools handlers
  const handleRpaFeatureChange = (lang, index, value) => {
    const key = `features_${lang}`;
    const newFeatures = [...rpaFormData[key]];
    newFeatures[index] = value;
    setRpaFormData({ ...rpaFormData, [key]: newFeatures });
  };

  const addRpaFeature = (lang) => {
    const key = `features_${lang}`;
    setRpaFormData({ ...rpaFormData, [key]: [...rpaFormData[key], ''] });
  };

  const removeRpaFeature = (lang, index) => {
    const key = `features_${lang}`;
    const newFeatures = rpaFormData[key].filter((_, i) => i !== index);
    setRpaFormData({ ...rpaFormData, [key]: newFeatures.length ? newFeatures : [''] });
  };

  // Technology handlers
  const handleTechChange = (index, value) => {
    const newTechs = [...techFormData.technologies];
    newTechs[index] = value;
    setTechFormData({ ...techFormData, technologies: newTechs });
  };

  const addTech = () => {
    setTechFormData({ ...techFormData, technologies: [...techFormData.technologies, ''] });
  };

  const removeTech = (index) => {
    const newTechs = techFormData.technologies.filter((_, i) => i !== index);
    setTechFormData({ ...techFormData, technologies: newTechs.length ? newTechs : [''] });
  };

  // Industry handlers
  const handleUseCaseChange = (lang, index, value) => {
    const key = `useCases_${lang}`;
    const newCases = [...industryFormData[key]];
    newCases[index] = value;
    setIndustryFormData({ ...industryFormData, [key]: newCases });
  };

  const addUseCase = (lang) => {
    const key = `useCases_${lang}`;
    setIndustryFormData({ ...industryFormData, [key]: [...industryFormData[key], ''] });
  };

  const removeUseCase = (lang, index) => {
    const key = `useCases_${lang}`;
    const newCases = industryFormData[key].filter((_, i) => i !== index);
    setIndustryFormData({ ...industryFormData, [key]: newCases.length ? newCases : [''] });
  };

  const resetForm = () => {
    setRpaFormData({
      name_en: '',
      name_fr: '',
      description_en: '',
      description_fr: '',
      features_en: [''],
      features_fr: [''],
      logoUrl: '',
      imageUrl: '',
      color: '#FA4616',
      order: activeSection === 'rpaTools' ? rpaTools.length : 0
    });
    setTechFormData({
      name_en: '',
      name_fr: '',
      description_en: '',
      description_fr: '',
      iconUrl: '',
      technologies: [''],
      order: activeSection === 'technologies' ? technologies.length : 0
    });
    setTechIconFile(null);
    setTechIconPreview('');
    if (techIconInputRef.current) techIconInputRef.current.value = '';
    setIndustryFormData({
      industry_en: '',
      industry_fr: '',
      useCases_en: [''],
      useCases_fr: [''],
      iconUrl: '',
      order: activeSection === 'industries' ? industries.length : 0
    });
    setIndustryIconFile(null);
    setIndustryIconPreview('');
    if (industryIconInputRef.current) industryIconInputRef.current.value = '';
    setImagePreview(null);
    setLogoPreview(null);
    setImageFile(null);
    setLogoFile(null);
    setEditingId(null);
    setShowForm(false);
    setActiveTab('en');
    setError('');
  };

  const handleEditRpa = (item) => {
    setRpaFormData({
      name_en: item.name_en || item.name || '',
      name_fr: item.name_fr || '',
      description_en: item.description_en || item.description || '',
      description_fr: item.description_fr || '',
      features_en: item.features_en?.length ? item.features_en : (item.features?.length ? item.features : ['']),
      features_fr: item.features_fr?.length ? item.features_fr : [''],
      logoUrl: item.logoUrl || '',
      imageUrl: item.imageUrl || '',
      color: item.color || '#FA4616',
      order: item.order || 0
    });
    setImagePreview(item.imageUrl || null);
    setLogoPreview(item.logoUrl || null);
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleEditTech = (item) => {
    setTechFormData({
      name_en: item.name_en || item.name || '',
      name_fr: item.name_fr || '',
      description_en: item.description_en || item.description || '',
      description_fr: item.description_fr || '',
      iconUrl: item.iconUrl || '',
      technologies: item.technologies?.length ? item.technologies : [''],
      order: item.order || 0
    });
    setTechIconPreview(item.iconUrl || '');
    setTechIconFile(null);
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleEditIndustry = (item) => {
    setIndustryFormData({
      industry_en: item.industry_en || item.industry || '',
      industry_fr: item.industry_fr || '',
      useCases_en: item.useCases_en?.length ? item.useCases_en : (item.useCases?.length ? item.useCases : ['']),
      useCases_fr: item.useCases_fr?.length ? item.useCases_fr : [''],
      iconUrl: item.iconUrl || '',
      order: item.order || 0
    });
    setIndustryIconPreview(item.iconUrl || '');
    setIndustryIconFile(null);
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleSubmitRpa = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!rpaFormData.name_en.trim() || !rpaFormData.description_en.trim()) {
      setError('English name and description are required.');
      setActiveTab('en');
      return;
    }

    setSaving(true);

    try {
      let imageUrl = rpaFormData.imageUrl;
      let logoUrl = rpaFormData.logoUrl;

      if (imageFile) {
        imageUrl = await uploadImage(imageFile, 'rpa-tools');
      }
      if (logoFile) {
        logoUrl = await uploadImage(logoFile, 'rpa-tools');
      }

      const data = {
        name_en: rpaFormData.name_en.trim(),
        name_fr: rpaFormData.name_fr.trim(),
        description_en: rpaFormData.description_en.trim(),
        description_fr: rpaFormData.description_fr.trim(),
        features_en: rpaFormData.features_en.filter(f => f.trim() !== ''),
        features_fr: rpaFormData.features_fr.filter(f => f.trim() !== ''),
        name: rpaFormData.name_en.trim(),
        description: rpaFormData.description_en.trim(),
        features: rpaFormData.features_en.filter(f => f.trim() !== ''),
        logoUrl,
        imageUrl,
        color: rpaFormData.color,
        order: rpaFormData.order,
        updatedAt: new Date().toISOString()
      };

      if (editingId) {
        await updateDoc(doc(db, 'solutions_rpa_tools', editingId), data);
        setSuccess('RPA Tool updated successfully!');
      } else {
        data.createdAt = new Date().toISOString();
        await addDoc(collection(db, 'solutions_rpa_tools'), data);
        setSuccess('RPA Tool added successfully!');
      }

      await fetchRpaTools();
      resetForm();
    } catch (err) {
      console.error('Error saving RPA tool:', err);
      setError(err.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitTech = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!techFormData.name_en.trim() || !techFormData.description_en.trim()) {
      setError('English name and description are required.');
      setActiveTab('en');
      return;
    }

    if (!techIconFile && !techFormData.iconUrl) {
      setError('Please upload an SVG icon.');
      return;
    }

    setSaving(true);

    try {
      let iconUrl = techFormData.iconUrl;

      if (techIconFile) {
        if (editingId && techFormData.iconUrl) {
          await deleteImage(techFormData.iconUrl);
        }
        iconUrl = await uploadImage(techIconFile, 'technologies');
      }

      const data = {
        name_en: techFormData.name_en.trim(),
        name_fr: techFormData.name_fr.trim(),
        description_en: techFormData.description_en.trim(),
        description_fr: techFormData.description_fr.trim(),
        name: techFormData.name_en.trim(),
        description: techFormData.description_en.trim(),
        iconUrl,
        technologies: techFormData.technologies.filter(t => t.trim() !== ''),
        order: techFormData.order,
        updatedAt: new Date().toISOString()
      };

      if (editingId) {
        await updateDoc(doc(db, 'solutions_technologies', editingId), data);
        setSuccess('Technology updated successfully!');
      } else {
        data.createdAt = new Date().toISOString();
        await addDoc(collection(db, 'solutions_technologies'), data);
        setSuccess('Technology added successfully!');
      }

      await fetchTechnologies();
      resetForm();
    } catch (err) {
      console.error('Error saving technology:', err);
      setError(err.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitIndustry = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!industryFormData.industry_en.trim()) {
      setError('English industry name is required.');
      setActiveTab('en');
      return;
    }

    if (!industryIconFile && !industryFormData.iconUrl) {
      setError('Please upload an icon image.');
      return;
    }

    setSaving(true);

    try {
      let iconUrl = industryFormData.iconUrl;

      if (industryIconFile) {
        if (editingId && industryFormData.iconUrl) {
          await deleteImage(industryFormData.iconUrl);
        }
        iconUrl = await uploadImage(industryIconFile, 'industries');
      }

      const data = {
        industry_en: industryFormData.industry_en.trim(),
        industry_fr: industryFormData.industry_fr.trim(),
        industry: industryFormData.industry_en.trim(),
        useCases_en: industryFormData.useCases_en.filter(u => u.trim() !== ''),
        useCases_fr: industryFormData.useCases_fr.filter(u => u.trim() !== ''),
        useCases: industryFormData.useCases_en.filter(u => u.trim() !== ''),
        iconUrl,
        order: industryFormData.order,
        updatedAt: new Date().toISOString()
      };

      if (editingId) {
        await updateDoc(doc(db, 'solutions_industries', editingId), data);
        setSuccess('Industry updated successfully!');
      } else {
        data.createdAt = new Date().toISOString();
        await addDoc(collection(db, 'solutions_industries'), data);
        setSuccess('Industry added successfully!');
      }

      await fetchIndustries();
      resetForm();
    } catch (err) {
      console.error('Error saving industry:', err);
      setError(err.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRpa = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.name_en || item.name}"?`)) {
      return;
    }

    try {
      if (item.imageUrl) await deleteImage(item.imageUrl);
      if (item.logoUrl) await deleteImage(item.logoUrl);
      await deleteDoc(doc(db, 'solutions_rpa_tools', item.id));
      setSuccess('RPA Tool deleted successfully!');
      await fetchRpaTools();
    } catch (err) {
      console.error('Error deleting:', err);
      setError('Failed to delete');
    }
  };

  const handleDeleteTech = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.name_en || item.name}"?`)) {
      return;
    }

    try {
      if (item.iconUrl) await deleteImage(item.iconUrl);
      await deleteDoc(doc(db, 'solutions_technologies', item.id));
      setSuccess('Technology deleted successfully!');
      await fetchTechnologies();
    } catch (err) {
      console.error('Error deleting:', err);
      setError('Failed to delete');
    }
  };

  const handleDeleteIndustry = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.industry_en || item.industry}"?`)) {
      return;
    }

    try {
      if (item.iconUrl) await deleteImage(item.iconUrl);
      await deleteDoc(doc(db, 'solutions_industries', item.id));
      setSuccess('Industry deleted successfully!');
      await fetchIndustries();
    } catch (err) {
      console.error('Error deleting:', err);
      setError('Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <FaSpinner className="spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) return null;

  const renderRpaToolsList = () => (
    <div className="solutions-list">
      {rpaTools.length === 0 ? (
        <div className="empty-state">
          <FaRobot />
          <h3>No RPA Tools yet</h3>
          <p>Click "Add New" to create your first RPA tool.</p>
        </div>
      ) : (
        rpaTools.map((item, index) => (
          <div key={item.id} className="solutions-item">
            <div className="solutions-item-order">
              <FaGripVertical />
              <span>{index + 1}</span>
            </div>
            <div className="solutions-item-image">
              {item.logoUrl ? (
                <img src={item.logoUrl} alt={item.name_en || item.name} />
              ) : (
                <div className="no-image"><FaImage /></div>
              )}
            </div>
            <div className="solutions-item-content">
              <div className="solutions-item-langs">
                <span className="lang-badge en">EN: {item.name_en || item.name || 'Not set'}</span>
                <span className="lang-badge fr">FR: {item.name_fr || 'Not set'}</span>
              </div>
              <p>{item.description_en || item.description}</p>
              <div className="color-indicator" style={{ backgroundColor: item.color }}></div>
            </div>
            <div className="solutions-item-actions">
              <button onClick={() => handleEditRpa(item)} className="edit-btn">
                <FaEdit /> Edit
              </button>
              <button onClick={() => handleDeleteRpa(item)} className="delete-btn">
                <FaTrash /> Delete
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );

  const renderTechnologiesList = () => (
    <div className="solutions-list">
      {technologies.length === 0 ? (
        <div className="empty-state">
          <FaBrain />
          <h3>No Technologies yet</h3>
          <p>Click "Add New" to create your first technology.</p>
        </div>
      ) : (
        technologies.map((item, index) => (
          <div key={item.id} className="solutions-item">
            <div className="solutions-item-order">
              <FaGripVertical />
              <span>{index + 1}</span>
            </div>
            <div className="solutions-item-icon">
              {item.iconUrl ? (
                <img src={item.iconUrl} alt="" className="solutions-icon-img" />
              ) : (
                <FaCogs />
              )}
            </div>
            <div className="solutions-item-content">
              <div className="solutions-item-langs">
                <span className="lang-badge en">EN: {item.name_en || item.name || 'Not set'}</span>
                <span className="lang-badge fr">FR: {item.name_fr || 'Not set'}</span>
              </div>
              <p>{item.description_en || item.description}</p>
              <div className="tech-tags">
                {item.technologies?.slice(0, 3).map((tech, idx) => (
                  <span key={idx} className="tech-tag">{tech}</span>
                ))}
                {item.technologies?.length > 3 && (
                  <span className="tech-tag more">+{item.technologies.length - 3}</span>
                )}
              </div>
            </div>
            <div className="solutions-item-actions">
              <button onClick={() => handleEditTech(item)} className="edit-btn">
                <FaEdit /> Edit
              </button>
              <button onClick={() => handleDeleteTech(item)} className="delete-btn">
                <FaTrash /> Delete
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );

  const renderIndustriesList = () => (
    <div className="solutions-list">
      {industries.length === 0 ? (
        <div className="empty-state">
          <FaIndustry />
          <h3>No Industries yet</h3>
          <p>Click "Add New" to create your first industry use case.</p>
        </div>
      ) : (
        industries.map((item, index) => (
          <div key={item.id} className="solutions-item">
            <div className="solutions-item-order">
              <FaGripVertical />
              <span>{index + 1}</span>
            </div>
            <div className="solutions-item-icon">
              {item.iconUrl ? (
                <img src={item.iconUrl} alt="" className="solutions-icon-img" />
              ) : (
                <FaIndustry />
              )}
            </div>
            <div className="solutions-item-content">
              <div className="solutions-item-langs">
                <span className="lang-badge en">EN: {item.industry_en || item.industry || 'Not set'}</span>
                <span className="lang-badge fr">FR: {item.industry_fr || 'Not set'}</span>
              </div>
              <div className="use-cases-preview">
                {(item.useCases_en || item.useCases)?.slice(0, 2).map((uc, idx) => (
                  <span key={idx} className="use-case-tag">{uc}</span>
                ))}
                {(item.useCases_en || item.useCases)?.length > 2 && (
                  <span className="use-case-tag more">+{(item.useCases_en || item.useCases).length - 2}</span>
                )}
              </div>
            </div>
            <div className="solutions-item-actions">
              <button onClick={() => handleEditIndustry(item)} className="edit-btn">
                <FaEdit /> Edit
              </button>
              <button onClick={() => handleDeleteIndustry(item)} className="delete-btn">
                <FaTrash /> Delete
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );

  const renderRpaForm = () => (
    <div className="solutions-form-container">
      <h2>{editingId ? 'Edit RPA Tool' : 'Add New RPA Tool'}</h2>

      <div className="language-tabs">
        <button
          type="button"
          className={`lang-tab ${activeTab === 'en' ? 'active' : ''}`}
          onClick={() => setActiveTab('en')}
        >
          <FaGlobe /> English
        </button>
        <button
          type="button"
          className={`lang-tab ${activeTab === 'fr' ? 'active' : ''}`}
          onClick={() => setActiveTab('fr')}
        >
          <FaGlobe /> French
        </button>
      </div>

      <form onSubmit={handleSubmitRpa} className="solutions-form">
        <div className={`lang-content ${activeTab === 'en' ? 'active' : ''}`}>
          <div className="form-group">
            <label>Name (English) *</label>
            <input
              type="text"
              value={rpaFormData.name_en}
              onChange={(e) => setRpaFormData({ ...rpaFormData, name_en: e.target.value })}
              placeholder="e.g., UiPath"
            />
          </div>

          <div className="form-group">
            <label>Description (English) *</label>
            <textarea
              value={rpaFormData.description_en}
              onChange={(e) => setRpaFormData({ ...rpaFormData, description_en: e.target.value })}
              placeholder="Describe this RPA tool..."
              rows={4}
            />
          </div>

          <div className="form-group">
            <label>Features (English)</label>
            {rpaFormData.features_en.map((feature, index) => (
              <div key={index} className="feature-input-row">
                <input
                  type="text"
                  value={feature}
                  onChange={(e) => handleRpaFeatureChange('en', index, e.target.value)}
                  placeholder={`Feature ${index + 1}`}
                />
                <button
                  type="button"
                  onClick={() => removeRpaFeature('en', index)}
                  className="remove-feature-btn"
                >
                  <FaTimes />
                </button>
              </div>
            ))}
            <button type="button" onClick={() => addRpaFeature('en')} className="add-feature-btn">
              <FaPlus /> Add Feature
            </button>
          </div>
        </div>

        <div className={`lang-content ${activeTab === 'fr' ? 'active' : ''}`}>
          <div className="form-group">
            <label>Name (French)</label>
            <input
              type="text"
              value={rpaFormData.name_fr}
              onChange={(e) => setRpaFormData({ ...rpaFormData, name_fr: e.target.value })}
              placeholder="e.g., UiPath"
            />
          </div>

          <div className="form-group">
            <label>Description (French)</label>
            <textarea
              value={rpaFormData.description_fr}
              onChange={(e) => setRpaFormData({ ...rpaFormData, description_fr: e.target.value })}
              placeholder="Décrivez cet outil RPA..."
              rows={4}
            />
          </div>

          <div className="form-group">
            <label>Features (French)</label>
            {rpaFormData.features_fr.map((feature, index) => (
              <div key={index} className="feature-input-row">
                <input
                  type="text"
                  value={feature}
                  onChange={(e) => handleRpaFeatureChange('fr', index, e.target.value)}
                  placeholder={`Fonctionnalité ${index + 1}`}
                />
                <button
                  type="button"
                  onClick={() => removeRpaFeature('fr', index)}
                  className="remove-feature-btn"
                >
                  <FaTimes />
                </button>
              </div>
            ))}
            <button type="button" onClick={() => addRpaFeature('fr')} className="add-feature-btn">
              <FaPlus /> Add Feature
            </button>
          </div>
        </div>

        <div className="common-fields">
          <div className="form-row">
            <div className="form-group">
              <label>Logo Image</label>
              <div className="image-upload-area small">
                {logoPreview ? (
                  <div className="image-preview">
                    <img src={logoPreview} alt="Logo Preview" />
                    <button
                      type="button"
                      onClick={() => {
                        setLogoPreview(null);
                        setLogoFile(null);
                        setRpaFormData({ ...rpaFormData, logoUrl: '' });
                      }}
                      className="remove-image-btn"
                    >
                      <FaTimes />
                    </button>
                  </div>
                ) : (
                  <label className="image-upload-label">
                    <FaImage />
                    <span>Upload Logo</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageChange(e, 'logo')}
                      hidden
                    />
                  </label>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>Background Image</label>
              <div className="image-upload-area small">
                {imagePreview ? (
                  <div className="image-preview">
                    <img src={imagePreview} alt="Preview" />
                    <button
                      type="button"
                      onClick={() => {
                        setImagePreview(null);
                        setImageFile(null);
                        setRpaFormData({ ...rpaFormData, imageUrl: '' });
                      }}
                      className="remove-image-btn"
                    >
                      <FaTimes />
                    </button>
                  </div>
                ) : (
                  <label className="image-upload-label">
                    <FaImage />
                    <span>Upload Image</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageChange(e, 'image')}
                      hidden
                    />
                  </label>
                )}
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Brand Color</label>
              <input
                type="color"
                value={rpaFormData.color}
                onChange={(e) => setRpaFormData({ ...rpaFormData, color: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Display Order</label>
              <input
                type="number"
                value={rpaFormData.order}
                onChange={(e) => setRpaFormData({ ...rpaFormData, order: parseInt(e.target.value) || 0 })}
                min="0"
              />
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" onClick={resetForm} className="cancel-btn">
            <FaTimes /> Cancel
          </button>
          <button type="submit" disabled={saving} className="save-btn">
            {saving ? (
              <><FaSpinner className="spinner" /> Saving...</>
            ) : (
              <><FaSave /> {editingId ? 'Update' : 'Save'}</>
            )}
          </button>
        </div>
      </form>
    </div>
  );

  const renderTechForm = () => (
    <div className="solutions-form-container">
      <h2>{editingId ? 'Edit Technology' : 'Add New Technology'}</h2>

      <div className="language-tabs">
        <button
          type="button"
          className={`lang-tab ${activeTab === 'en' ? 'active' : ''}`}
          onClick={() => setActiveTab('en')}
        >
          <FaGlobe /> English
        </button>
        <button
          type="button"
          className={`lang-tab ${activeTab === 'fr' ? 'active' : ''}`}
          onClick={() => setActiveTab('fr')}
        >
          <FaGlobe /> French
        </button>
      </div>

      <form onSubmit={handleSubmitTech} className="solutions-form">
        <div className={`lang-content ${activeTab === 'en' ? 'active' : ''}`}>
          <div className="form-group">
            <label>Name (English) *</label>
            <input
              type="text"
              value={techFormData.name_en}
              onChange={(e) => setTechFormData({ ...techFormData, name_en: e.target.value })}
              placeholder="e.g., Artificial Intelligence"
            />
          </div>

          <div className="form-group">
            <label>Description (English) *</label>
            <textarea
              value={techFormData.description_en}
              onChange={(e) => setTechFormData({ ...techFormData, description_en: e.target.value })}
              placeholder="Describe this technology..."
              rows={4}
            />
          </div>
        </div>

        <div className={`lang-content ${activeTab === 'fr' ? 'active' : ''}`}>
          <div className="form-group">
            <label>Name (French)</label>
            <input
              type="text"
              value={techFormData.name_fr}
              onChange={(e) => setTechFormData({ ...techFormData, name_fr: e.target.value })}
              placeholder="e.g., Intelligence Artificielle"
            />
          </div>

          <div className="form-group">
            <label>Description (French)</label>
            <textarea
              value={techFormData.description_fr}
              onChange={(e) => setTechFormData({ ...techFormData, description_fr: e.target.value })}
              placeholder="Décrivez cette technologie..."
              rows={4}
            />
          </div>
        </div>

        <div className="common-fields">
          <div className="form-group">
            <label>Icon (SVG) *</label>
            <div className="icon-upload-area">
              {techIconPreview ? (
                <div className="icon-preview-container">
                  <img src={techIconPreview} alt="Icon preview" className="icon-preview-img" />
                  <button
                    type="button"
                    className="icon-remove-btn"
                    onClick={() => {
                      setTechIconFile(null);
                      setTechIconPreview('');
                      setTechFormData({ ...techFormData, iconUrl: '' });
                      if (techIconInputRef.current) techIconInputRef.current.value = '';
                    }}
                  >
                    <FaTimes />
                  </button>
                </div>
              ) : (
                <div
                  className="icon-upload-placeholder"
                  onClick={() => techIconInputRef.current?.click()}
                >
                  <FaUpload />
                  <span>Click to upload SVG icon</span>
                  <small>Max 500KB, SVG format only</small>
                </div>
              )}
              <input
                ref={techIconInputRef}
                type="file"
                accept=".svg,image/svg+xml"
                onChange={handleTechIconChange}
                className="icon-file-input"
              />
              {techIconPreview && (
                <button
                  type="button"
                  className="icon-change-btn"
                  onClick={() => techIconInputRef.current?.click()}
                >
                  <FaUpload /> Change Icon
                </button>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>Technologies / Tools</label>
            {techFormData.technologies.map((tech, index) => (
              <div key={index} className="feature-input-row">
                <input
                  type="text"
                  value={tech}
                  onChange={(e) => handleTechChange(index, e.target.value)}
                  placeholder={`Technology ${index + 1}`}
                />
                <button
                  type="button"
                  onClick={() => removeTech(index)}
                  className="remove-feature-btn"
                >
                  <FaTimes />
                </button>
              </div>
            ))}
            <button type="button" onClick={addTech} className="add-feature-btn">
              <FaPlus /> Add Technology
            </button>
          </div>

          <div className="form-group">
            <label>Display Order</label>
            <input
              type="number"
              value={techFormData.order}
              onChange={(e) => setTechFormData({ ...techFormData, order: parseInt(e.target.value) || 0 })}
              min="0"
            />
          </div>
        </div>

        <div className="form-actions">
          <button type="button" onClick={resetForm} className="cancel-btn">
            <FaTimes /> Cancel
          </button>
          <button type="submit" disabled={saving} className="save-btn">
            {saving ? (
              <><FaSpinner className="spinner" /> Saving...</>
            ) : (
              <><FaSave /> {editingId ? 'Update' : 'Save'}</>
            )}
          </button>
        </div>
      </form>
    </div>
  );

  const renderIndustryForm = () => (
    <div className="solutions-form-container">
      <h2>{editingId ? 'Edit Industry' : 'Add New Industry'}</h2>

      <div className="language-tabs">
        <button
          type="button"
          className={`lang-tab ${activeTab === 'en' ? 'active' : ''}`}
          onClick={() => setActiveTab('en')}
        >
          <FaGlobe /> English
        </button>
        <button
          type="button"
          className={`lang-tab ${activeTab === 'fr' ? 'active' : ''}`}
          onClick={() => setActiveTab('fr')}
        >
          <FaGlobe /> French
        </button>
      </div>

      <form onSubmit={handleSubmitIndustry} className="solutions-form">
        <div className={`lang-content ${activeTab === 'en' ? 'active' : ''}`}>
          <div className="form-group">
            <label>Industry Name (English) *</label>
            <input
              type="text"
              value={industryFormData.industry_en}
              onChange={(e) => setIndustryFormData({ ...industryFormData, industry_en: e.target.value })}
              placeholder="e.g., Finance & Banking"
            />
          </div>

          <div className="form-group">
            <label>Use Cases (English)</label>
            {industryFormData.useCases_en.map((uc, index) => (
              <div key={index} className="feature-input-row">
                <input
                  type="text"
                  value={uc}
                  onChange={(e) => handleUseCaseChange('en', index, e.target.value)}
                  placeholder={`Use Case ${index + 1}`}
                />
                <button
                  type="button"
                  onClick={() => removeUseCase('en', index)}
                  className="remove-feature-btn"
                >
                  <FaTimes />
                </button>
              </div>
            ))}
            <button type="button" onClick={() => addUseCase('en')} className="add-feature-btn">
              <FaPlus /> Add Use Case
            </button>
          </div>
        </div>

        <div className={`lang-content ${activeTab === 'fr' ? 'active' : ''}`}>
          <div className="form-group">
            <label>Industry Name (French)</label>
            <input
              type="text"
              value={industryFormData.industry_fr}
              onChange={(e) => setIndustryFormData({ ...industryFormData, industry_fr: e.target.value })}
              placeholder="e.g., Finance & Banque"
            />
          </div>

          <div className="form-group">
            <label>Use Cases (French)</label>
            {industryFormData.useCases_fr.map((uc, index) => (
              <div key={index} className="feature-input-row">
                <input
                  type="text"
                  value={uc}
                  onChange={(e) => handleUseCaseChange('fr', index, e.target.value)}
                  placeholder={`Cas d'usage ${index + 1}`}
                />
                <button
                  type="button"
                  onClick={() => removeUseCase('fr', index)}
                  className="remove-feature-btn"
                >
                  <FaTimes />
                </button>
              </div>
            ))}
            <button type="button" onClick={() => addUseCase('fr')} className="add-feature-btn">
              <FaPlus /> Add Use Case
            </button>
          </div>
        </div>

        <div className="common-fields">
          <div className="form-group">
            <label>Icon Image *</label>
            <div className="icon-upload-area">
              {industryIconPreview ? (
                <div className="icon-preview-container">
                  <img src={industryIconPreview} alt="Icon preview" className="icon-preview-img" />
                  <button
                    type="button"
                    className="icon-remove-btn"
                    onClick={() => {
                      setIndustryIconFile(null);
                      setIndustryIconPreview('');
                      setIndustryFormData({ ...industryFormData, iconUrl: '' });
                      if (industryIconInputRef.current) industryIconInputRef.current.value = '';
                    }}
                  >
                    <FaTimes />
                  </button>
                </div>
              ) : (
                <div
                  className="icon-upload-placeholder"
                  onClick={() => industryIconInputRef.current?.click()}
                >
                  <FaUpload />
                  <span>Click to upload icon image</span>
                  <small>Max 2MB (SVG, PNG, JPG, WebP)</small>
                </div>
              )}
              <input
                ref={industryIconInputRef}
                type="file"
                accept="image/*"
                onChange={handleIndustryIconChange}
                className="icon-file-input"
              />
              {industryIconPreview && (
                <button
                  type="button"
                  className="icon-change-btn"
                  onClick={() => industryIconInputRef.current?.click()}
                >
                  <FaUpload /> Change Image
                </button>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>Display Order</label>
            <input
              type="number"
              value={industryFormData.order}
              onChange={(e) => setIndustryFormData({ ...industryFormData, order: parseInt(e.target.value) || 0 })}
              min="0"
            />
          </div>
        </div>

        <div className="form-actions">
          <button type="button" onClick={resetForm} className="cancel-btn">
            <FaTimes /> Cancel
          </button>
          <button type="submit" disabled={saving} className="save-btn">
            {saving ? (
              <><FaSpinner className="spinner" /> Saving...</>
            ) : (
              <><FaSave /> {editingId ? 'Update' : 'Save'}</>
            )}
          </button>
        </div>
      </form>
    </div>
  );

  return (
    <div className="admin-solutions">
      <header className="admin-solutions-header">
        <button onClick={() => navigate('/admin/dashboard')} className="back-btn">
          <FaArrowLeft /> Back to Dashboard
        </button>
        <h1><FaCogs /> Solutions Management</h1>
      </header>

      <main className="admin-solutions-content">
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {/* Section Tabs */}
        <div className="section-tabs">
          <button
            className={`section-tab ${activeSection === 'rpaTools' ? 'active' : ''}`}
            onClick={() => { setActiveSection('rpaTools'); resetForm(); }}
          >
            <FaRobot /> RPA Tools
            <span className="count">{rpaTools.length}</span>
          </button>
          <button
            className={`section-tab ${activeSection === 'technologies' ? 'active' : ''}`}
            onClick={() => { setActiveSection('technologies'); resetForm(); }}
          >
            <FaBrain /> Technologies
            <span className="count">{technologies.length}</span>
          </button>
          <button
            className={`section-tab ${activeSection === 'industries' ? 'active' : ''}`}
            onClick={() => { setActiveSection('industries'); resetForm(); }}
          >
            <FaIndustry /> Industries
            <span className="count">{industries.length}</span>
          </button>
        </div>

        {!showForm ? (
          <>
            <div className="solutions-actions">
              <button onClick={() => setShowForm(true)} className="add-btn">
                <FaPlus /> Add New {activeSection === 'rpaTools' ? 'RPA Tool' : activeSection === 'technologies' ? 'Technology' : 'Industry'}
              </button>
            </div>

            {activeSection === 'rpaTools' && renderRpaToolsList()}
            {activeSection === 'technologies' && renderTechnologiesList()}
            {activeSection === 'industries' && renderIndustriesList()}
          </>
        ) : (
          <>
            {activeSection === 'rpaTools' && renderRpaForm()}
            {activeSection === 'technologies' && renderTechForm()}
            {activeSection === 'industries' && renderIndustryForm()}
          </>
        )}
      </main>
    </div>
  );
};

export default AdminSolutions;
